<template>
  <custom-dialog
    :is-dialog-open="isDialogOpen"
    :close-dialog="closeDialog"
    :dialog-title="dialogTitle"
    :max-width="dialogMaxWidth"
  >
    <v-form
      ref="formRef"
      class="multi-col-validation"
      @submit.prevent="handleSubmitBtnClick"
    >
      <v-row class="align-center">
        <v-col :cols="qtdColunas.form">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="grupoContatosDataLocale.nome"
                label="Nome"
                dense
                outlined
                validate-on-blur
                :rules="[validators.required, v => (!v || v.length <= 50) || 'Máximo de 50 caracteres']"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="grupoContatosDataLocale.descricao"
                label="Descrição"
                dense
                outlined
                validate-on-blur
                no-resize
                :rules="[v => (!v || v.length <= 500) || 'Máximo de 500 caracteres']"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="auto">
              <v-switch
                v-model="grupoContatosDataLocale.isEmail"
                label="E-mail"
                hide-details
                class="mt-0"
                :disabled="realizouUploadDoExcel"
              />
            </v-col>

            <v-col cols="auto">
              <v-switch
                v-model="grupoContatosDataLocale.isCelular"
                label="Celular"
                hide-details
                class="mt-0"
                :disabled="realizouUploadDoExcel"
              />
            </v-col>
          </v-row>

          <v-expand-transition>
            <v-row v-if="grupoContatosDataLocale.isEmail || grupoContatosDataLocale.isCelular">
              <v-col cols="12">
                <v-card
                  outlined
                  elevation="4"
                  class="d-flex flex-column pa-2 mt-5"
                >
                  <div class="align-self-end d-flex gap-2">
                    <v-btn
                      v-if="!isNovoGrupoContato && contatosOriginais.length"
                      outlined
                      color="success"
                      class="mr-2"
                      @click="exportarExcel"
                    >
                      <v-icon left>
                        {{ icons.mdiMicrosoftExcel }}
                      </v-icon>
                      <span>Exportar contatos</span>
                    </v-btn>

                    <v-btn
                      outlined
                      download
                      color="primary"
                      :href="linkExcelExemplo"
                    >
                      <v-icon left>
                        {{ icons.mdiMicrosoftExcel }}
                      </v-icon>
                      <span>Download Excel de exemplo</span>
                    </v-btn>
                  </div>

                  <v-file-input
                    ref="excelInputRef"
                    class="mt-3"
                    show-size
                    validate-on-blur
                    label="Importar Excel"
                    accept=".xlsx, .xlsm, .xltx, .xltm, .xlsb, .xlam"
                    @change="uploadExcel"
                    @click:clear="handleClearExcel"
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-expand-transition>
        </v-col>

        <v-expand-transition>
          <v-col
            v-if="grupoContatosExcelDataFormatado.length"
            :cols="qtdColunas.table"
          >
            <custom-table
              :headers="excelTableHeaders"
              :items="grupoContatosExcelDataFormatado"
              :items-per-page="10"
              hide-default-footer
            />
          </v-col>
        </v-expand-transition>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          class="d-flex justify-center"
        >
          <v-btn
            color="secondary"
            outlined
            class="mr-4"
            @click="resetFormAndCloseDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            type="submit"
          >
            <v-progress-circular
              v-if="loading"
              indeterminate
              color="secondary"
              class="mx-6"
            />
            <span v-else>Salvar</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </custom-dialog>
</template>

<script>
import Vue from 'vue'
import * as XLSX from 'xlsx'
import { mdiPaperclip, mdiMicrosoftExcel } from '@mdi/js'
import { ref, computed, watch } from '@vue/composition-api'
import { emailValidator, required } from '@/@core/utils/validation'
import CustomDialog from '@/components/dialog/Dialog.vue'
import CustomTable from '@/components/tables/Table.vue'
import GruposContatosService from '@/services/modules/GruposContatosService'
import GrupoContatosModel from '@/models/entities/grupo-contatos/GrupoContatosModel'
import useDialog from '@/composables/useDialog'
import { isCpfValido, isTelefoneCelularValido } from '@/utils/validation'
import { formatarCelular, formatarCpf, formatarCnpj } from '@/utils/formatting'

export default {
  components: {
    CustomDialog,
    CustomTable,
  },

  emits: ['update:isDialogOpen'],

  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    grupoContatosData: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const grupoContatosDataLocale = ref(new GrupoContatosModel())

    const excelInputRef = ref(null)
    const grupoContatosExcelData = ref([])
    const grupoContatosExcelDataFormatado = ref([])
    const excelTableHeaders = [
      { text: 'Nome', value: 'nome' },
      { text: 'E-mail', value: 'email' },
      { text: 'Celular', value: 'celular' },
      { text: 'CPF', value: 'cpf' },
      { text: 'CNPJ', value: 'cnpj' },
      { text: 'Empresa', value: 'empresa' },
    ]

    const qtdColunas = computed(() => ({
      form: grupoContatosExcelDataFormatado.value.length ? 6 : 12,
      table: grupoContatosExcelDataFormatado.value.length ? 6 : 0,
    }))

    // Contatos originais do grupo (quando editando) — usados como fallback
    // se o usuário fizer upload de Excel e depois limpar o input
    const contatosOriginais = ref([])

    const isNovoGrupoContato = computed(() => !props.grupoContatosData)
    const dialogTitle = computed(() => (isNovoGrupoContato.value ? 'Criar grupo de contatos' : 'Editar grupos de contatos'))
    const dialogMaxWidth = computed(() => (grupoContatosExcelDataFormatado.value.length ? null : '800px'))
    const realizouUploadDoExcel = computed(() => grupoContatosExcelData.value.length >= 1)

    watch(
      () => props.grupoContatosData,
      v => {
        grupoContatosDataLocale.value = v
          ? JSON.parse(JSON.stringify(v))
          : new GrupoContatosModel()

        // Ao abrir o dialog de edição, exibe os contatos existentes na tabela
        const contatos = v?.contatos ?? []
        contatosOriginais.value = contatos

        if (contatos.length) {
          grupoContatosExcelData.value = contatos
          grupoContatosExcelDataFormatado.value = contatos
            .map(it => ({
              ...it,
              celular: formatarCelular(it.celular),
              cpf: formatarCpf(it.cpf),
              cnpj: formatarCnpj(it.cnpj),
            }))
            .slice(0, 10)
        } else {
          grupoContatosExcelData.value = []
          grupoContatosExcelDataFormatado.value = []
        }
      },
      { immediate: true, deep: true },
    )

    const { formRef, loading, resetForm, validateForm, closeDialog, emitRefetchData } = useDialog(emit, 'isDialogOpen')

    // ─── Helpers ──────────────────────────────────────────────────────────────

    // Normaliza string para comparação de cabeçalho (minúsculo, sem acento)
    function normalizeHeader(str) {
      return String(str ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
    }

    // Retorna valor da célula como string ou null
    function getCol(linha, idx) {
      if (idx < 0 || linha[idx] == null) return null
      const val = String(linha[idx]).trim()
      return val.length > 0 ? val : null
    }

    // ─── Excel ────────────────────────────────────────────────────────────────

    function resetExcelData() {
      if (excelInputRef.value) excelInputRef.value.reset()
      grupoContatosExcelData.value = []
      grupoContatosExcelDataFormatado.value = []
    }

    function resetFormAndCloseDialog() {
      contatosOriginais.value = []
      resetExcelData()
      resetForm()
      closeDialog()
    }

    function isExcelValido(data) {
      // data já é array de objetos { nome, email, celular, cpf, empresa }
      const emails = data.map(it => it.email).filter(it => !!it)
      const celulares = data.map(it => it.celular).filter(it => !!it)
      const cpfs = data.map(it => it.cpf).filter(it => !!it)

      const qtdEmailsInvalidos = emails.filter(it => emailValidator(it) !== true).length
      const qtdCelularesInvalidos = celulares.filter(it => isTelefoneCelularValido(it) !== true).length
      const qtdCpfsInvalidos = cpfs.filter(it => isCpfValido(it) !== true).length

      if (!qtdEmailsInvalidos && !qtdCelularesInvalidos && !qtdCpfsInvalidos) return true

      let msg = 'Sua planilha de importação possui:\n'

      if (qtdEmailsInvalidos) {
        msg += qtdEmailsInvalidos === 1
          ? `\n${qtdEmailsInvalidos} e-mail com erro de digitação`
          : `\n${qtdEmailsInvalidos} e-mails com erros de digitação`
      }

      if (qtdCelularesInvalidos) {
        msg += qtdCelularesInvalidos === 1
          ? `\n${qtdCelularesInvalidos} celular com erro de digitação`
          : `\n${qtdCelularesInvalidos} celulares com erros de digitação`
      }

      if (qtdCpfsInvalidos) {
        msg += qtdCpfsInvalidos === 1
          ? `\n${qtdCpfsInvalidos} cpf com erro de digitação`
          : `\n${qtdCpfsInvalidos} cpfs com erros de digitação`
      }

      msg += '\n\nNenhum dado foi importado, corrija sua base e repita o processo.'

      Vue.$toast(msg)

      return false
    }

    function uploadExcel(file) {
      if (!file) return

      const reader = new FileReader()

      reader.onload = e => {
        const workbook = XLSX.read(e.target.result, { type: 'binary' })
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]

        // raw: false → tudo retorna como string (resolve número em celular/CPF)
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false })

        if (!rows || rows.length < 2) {
          Vue.$toast.warning('A planilha está vazia.')
          return
        }

        // Detecta índice de cada coluna pelo nome do cabeçalho
        // Funciona independente da ordem ou de colunas ausentes
        const header = rows[0].map(normalizeHeader)
        const idx = {
          nome: header.findIndex(h => h === 'nome'),
          email: header.findIndex(h => h === 'email'),
          celular: header.findIndex(h => h === 'celular'),
          cpf: header.findIndex(h => h === 'cpf'),
          cnpj: header.findIndex(h => h === 'cnpj'),
          empresa: header.findIndex(h => h === 'empresa'),
        }

        const data = rows
          .slice(1)
          .filter(linha => {
            // Mantém linha se tiver ao menos email ou celular
            const email = getCol(linha, idx.email) ?? ''
            const celular = getCol(linha, idx.celular) ?? ''
            return email.length > 0 || celular.length > 0
          })
          .map(linha => ({
            nome: getCol(linha, idx.nome),
            email: getCol(linha, idx.email)?.toLowerCase(),
            celular: getCol(linha, idx.celular),
            cpf: getCol(linha, idx.cpf),
            cnpj: getCol(linha, idx.cnpj),
            empresa: getCol(linha, idx.empresa),
          }))

        if (!isExcelValido(data)) return

        grupoContatosExcelData.value = data

        grupoContatosExcelDataFormatado.value = data
          .map(it => ({
            ...it,
            celular: formatarCelular(it.celular),
            cpf: formatarCpf(it.cpf),
            cnpj: formatarCnpj(it.cnpj),
          }))
          .slice(0, 10)
      }

      reader.readAsBinaryString(file)
    }

    function handleClearExcel() {
      // Se está editando e havia contatos originais, restaura-os na tabela
      // para não enviar lista vazia ao salvar sem novo Excel
      if (!isNovoGrupoContato.value && contatosOriginais.value.length) {
        grupoContatosExcelData.value = contatosOriginais.value
        grupoContatosExcelDataFormatado.value = contatosOriginais.value
          .map(it => ({
            ...it,
            celular: formatarCelular(it.celular),
            cpf: formatarCpf(it.cpf),
            cnpj: formatarCnpj(it.cnpj),
          }))
          .slice(0, 10)
        if (excelInputRef.value) excelInputRef.value.reset()
        return
      }

      resetExcelData()
    }

    // ─── Exportar Excel ───────────────────────────────────────────────────────

    function exportarExcel() {
      const dados = contatosOriginais.value.length ? contatosOriginais.value : grupoContatosExcelData.value

      const rows = dados.map(it => ({
        Nome: it.nome || '',
        Email: it.email || '',
        Celular: it.celular || '',
        CPF: it.cpf || '',
        CNPJ: it.cnpj || '',
        Empresa: it.empresa || '',
      }))

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Contatos')
      XLSX.writeFile(wb, `${grupoContatosDataLocale.value.nome || 'grupo-contatos'}.xlsx`)
    }

    // ─── Submit ───────────────────────────────────────────────────────────────

    function getRequestBody() {
      return {
        ...grupoContatosDataLocale.value,
        contatos: grupoContatosExcelData.value,
      }
    }

    async function handleSubmitBtnClick() {
      if (loading.value) return
      if (!validateForm()) return

      try {
        loading.value = true

        const body = getRequestBody()

        if (isNovoGrupoContato.value) {
          await GruposContatosService.post(body)
        } else {
          await GruposContatosService.put(grupoContatosDataLocale.value.id, body)
        }

        emitRefetchData()
        resetFormAndCloseDialog()
      } finally {
        loading.value = false
      }
    }

    // ─── Constants ────────────────────────────────────────────────────────────

    const linkExcelExemplo = 'https://vikdocumentdb.blob.core.windows.net/utils/TemplateDadosGrupoContatos3.xlsx?sv=2025-07-05&spr=https&st=2026-05-08T14%3A49%3A33Z&se=2030-05-09T14%3A49%3A00Z&sr=b&sp=r&sig=YMWJLaAWvpGwLdfI9gXdQVY6uLEFb9JzsUXTUPbJ2%2Bg%3D'

    return {
      grupoContatosDataLocale,
      excelInputRef,
      grupoContatosExcelDataFormatado,
      excelTableHeaders,
      qtdColunas,
      dialogTitle,
      dialogMaxWidth,
      realizouUploadDoExcel,
      contatosOriginais,
      isNovoGrupoContato,
      formRef,
      loading,
      resetFormAndCloseDialog,
      closeDialog,
      uploadExcel,
      handleClearExcel,
      handleSubmitBtnClick,
      exportarExcel,
      validators: { required },
      icons: { mdiPaperclip, mdiMicrosoftExcel },
      linkExcelExemplo,
    }
  },
}
</script>
